import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Popper, Stack, Typography } from '@mui/material';

/**
 * One stop on a tour: a section of the screen and what to say about it.
 *
 * `target` names a `data-tour` attribute rather than holding a ref, so the
 * screens being toured only have to mark their sections — they do not have to
 * know a tour exists, or thread refs up to the shell that runs it. Several
 * elements may carry the same name; the highlight then covers all of them.
 *
 * No target means a card in the middle of the screen, for an introduction or
 * a closing word that is about the screen as a whole.
 */
export interface TourStep {
  target?: string;
  title: string;
  body: string;
}

const PAD = 6;
const Z = 1500;

function targetsOf(name: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`));
}

/** The union of every visible element carrying the name, or null if none is. */
function rectOf(name: string): DOMRect | null {
  const rects = targetsOf(name)
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return new DOMRect(left - PAD, top - PAD, right - left + PAD * 2, bottom - top + PAD * 2);
}

/**
 * A step-by-step walk through the screen: the page dimmed, one section lit,
 * and a card beside it with Back, Next and Skip.
 *
 * Steps whose section is not on screen when the tour opens are dropped rather
 * than shown pointing at nothing, so a tour describes the screen as it
 * actually is — whatever state it is in and whatever its width.
 */
export function GuidedTour({
  open, steps, onClose,
}: {
  open: boolean;
  steps: TourStep[];
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const popper = useRef<{ update(): unknown } | null>(null);

  // Decided once per opening, so "3 of 9" stays true for the whole tour.
  const available = useMemo(
    () => (open ? steps.filter((s) => !s.target || rectOf(s.target)) : []),
    [open, steps],
  );
  const step = available[index];
  const last = index === available.length - 1;

  useEffect(() => { if (open) setIndex(0); }, [open]);

  // Bring the section into view, scrolling whichever container holds it.
  useEffect(() => {
    if (!step?.target) return;
    targetsOf(step.target)[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [step]);

  /*
   * Measured every frame while open. The section moves during the smooth
   * scroll above, when the window is resized, and when content around it
   * changes size; following all of those individually is more code than this
   * and misses whichever one was forgotten.
   */
  useEffect(() => {
    if (!open || !step?.target) { setRect(null); return; }
    let frame = 0;
    const tick = () => {
      const next = rectOf(step.target!);
      setRect((prev) =>
        prev && next && prev.x === next.x && prev.y === next.y
          && prev.width === next.width && prev.height === next.height
          ? prev
          : next);
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [open, step]);

  useEffect(() => { popper.current?.update(); }, [rect]);

  const next = useCallback(() => {
    if (last) onClose(); else setIndex((i) => i + 1);
  }, [last, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') next();
      else if (event.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, back, onClose]);

  if (!open || !step) return null;

  const anchor = rect
    ? { getBoundingClientRect: () => rect }
    : null;

  const card = (
    <Paper elevation={8} sx={{ p: 2, width: 320, maxWidth: 'calc(100vw - 32px)' }}>
      <Typography variant="caption" color="text.secondary">
        {index + 1} of {available.length}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.25 }}>
        {step.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {step.body}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        {!last && (
          <Button size="small" color="inherit" onClick={onClose}>Skip</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {index > 0 && <Button size="small" onClick={back}>Back</Button>}
        <Button size="small" variant="contained" onClick={next} autoFocus>
          {last ? 'Done' : 'Next'}
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <>
      {/* Swallows clicks, so the tour is not driven from underneath. */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: Z }} />

      {rect ? (
        <Box
          sx={{
            position: 'fixed',
            left: rect.x, top: rect.y, width: rect.width, height: rect.height,
            borderRadius: 1.5,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
            outline: 2, outlineColor: 'primary.main',
            zIndex: Z,
            pointerEvents: 'none',
            transition: 'left 200ms, top 200ms, width 200ms, height 200ms',
          }}
        />
      ) : (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.55)', zIndex: Z }} />
      )}

      {anchor ? (
        <Popper
          open
          anchorEl={anchor}
          placement="right-start"
          popperRef={(instance) => { popper.current = instance; }}
          modifiers={[
            { name: 'offset', options: { offset: [0, 12] } },
            { name: 'flip', options: { fallbackPlacements: ['bottom', 'top', 'left'] } },
            { name: 'preventOverflow', options: { padding: 16 } },
          ]}
          sx={{ zIndex: Z + 1 }}
        >
          {card}
        </Popper>
      ) : (
        <Box
          sx={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', zIndex: Z + 1,
          }}
        >
          {card}
        </Box>
      )}
    </>
  );
}
